import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronLeft, X, ExternalLink, Share2, Bookmark, MessageCircle, 
  Heart, Calendar, Clock, Tag, Loader2, User, ChevronRight, Eye 
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import CommentsSection from './CommentsSection';
import Footer from './Footer';
import AuthorPanel from './AuthorPanel';
import AuthModal from './AuthModal';
import SuggestedArticlesSidebar from './SuggestedArticlesSidebar';
import { saveArticleLocally, isArticleSavedLocally } from './LibraryPage';
import { useSwipeToClose } from '../lib/useSwipeToClose';
import { parseDate, getTimeAgo, formatDisplayDate } from '../lib/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatDate = (dateString) => formatDisplayDate(dateString);

const calculateReadTime = (content) => {
  if (!content) return '1 min read';
  const words = content.split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return `${minutes} min read`;
};

const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 100);
};

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Likes & Comments state
  const [likes, setLikes] = useState(0);
  const [userLiked, setUserLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [commentCount, setCommentCount] = useState(0);

  // Bookmark & Author state
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const [authorData, setAuthorData] = useState(null);
  const [authorPanelOpen, setAuthorPanelOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Related, recent and trending articles for suggested sidebar
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [trendingArticles, setTrendingArticles] = useState([]);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => 
    typeof window !== 'undefined' ? (window.innerWidth < 768 || window.matchMedia('(max-width: 767px)').matches) : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768 || window.matchMedia('(max-width: 767px)').matches);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // On mobile, redirect to home with article query param so the seamless ReaderPanel is used
  useEffect(() => {
    if (isMobile && slug) {
      navigate(`/?article=${slug}`, { replace: true });
    }
  }, [isMobile, slug, navigate]);

  // Animation lifecycle state
  const [isExiting, setIsExiting] = useState(false);
  const [exitConfig, setExitConfig] = useState(null);
  const isClosingRef = useRef(false);

  // Smooth dismiss handler: on mobile slides back out to the right before navigating
  const handleDismiss = useCallback((options = {}) => {
    if (authorPanelOpen) {
      setAuthorPanelOpen(false);
      return;
    }
    if (isClosingRef.current) return;
    isClosingRef.current = true;

    if (isMobile) {
      const fromSwipe = Boolean(options?.fromSwipe);
      const startOffset = options?.currentOffset || 0;
      const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 360;
      const remainingDist = Math.max(0, screenWidth - startOffset);
      const duration = fromSwipe && startOffset > 0
        ? Math.max(130, Math.min(240, Math.round((remainingDist / screenWidth) * 260)))
        : 240;

      setExitConfig({ duration, fromSwipe, startOffset });
      setIsExiting(true);

      setTimeout(() => {
        isClosingRef.current = false;
        if (window.history.length > 2) {
          navigate(-1);
        } else {
          navigate('/');
        }
      }, duration);
    } else {
      isClosingRef.current = false;
      if (window.history.length > 2) {
        navigate(-1);
      } else {
        navigate('/');
      }
    }
  }, [authorPanelOpen, isMobile, navigate]);

  // Reset author panel whenever slug changes
  useEffect(() => {
    setAuthorPanelOpen(false);
  }, [slug]);

  const { handlers: swipeHandlers, swipeOffset, isSwiping, isSnappingBack } = useSwipeToClose(handleDismiss, !authorPanelOpen, isMobile);

  // Fetch article data
  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${BACKEND_URL}/api/article/${slug}`);
        const art = response.data;
        setArticle(art);
        setCommentCount(art.comments_count || 0);

        const articleDbId = art.id || art._id;
        if (articleDbId) {
          // Fetch live comments count
          axios.get(`${BACKEND_URL}/api/comments/${articleDbId}`)
            .then(res => {
              if (Array.isArray(res.data)) {
                setCommentCount(res.data.length);
              }
            })
            .catch(() => {});

          // Fetch reactions
          const token = localStorage.getItem('yegoNewsToken');
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          axios.get(`${BACKEND_URL}/api/articles/${art.id}/reactions`, { headers })
            .then(res => {
              setLikes(res.data.like_count || 0);
              setUserLiked(res.data.user_liked || false);
            })
            .catch(() => {});

          // Fetch related, recent & trending stories for YouTube-style suggested sidebar
          axios.get(`${BACKEND_URL}/api/articles/related/${art.id}?limit=8`)
            .then(res => setRelatedArticles(res.data || []))
            .catch(() => setRelatedArticles([]));

          axios.get(`${BACKEND_URL}/api/articles/recent?limit=8&exclude_id=${art.id}`)
            .then(res => setRecentArticles(res.data || []))
            .catch(() => setRecentArticles([]));

          axios.get(`${BACKEND_URL}/api/articles/trending?limit=10`)
            .then(res => setTrendingArticles(res.data || []))
            .catch(() => {
              // Fallback to my-stories if trending has no dedicated table
              axios.get(`${BACKEND_URL}/api/my-stories?limit=12`)
                .then(res => {
                  const list = (res.data?.articles || []).filter(a => a.id !== art.id);
                  setTrendingArticles(list);
                })
                .catch(() => setTrendingArticles([]));
            });
        }
      } catch (err) {
        console.error('Error fetching article:', err);
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticle();
  }, [slug]);

  // Check bookmark status
  useEffect(() => {
    if (!article) return;

    const checkBookmarkStatus = () => {
      if (isArticleSavedLocally(article)) {
        setIsBookmarked(true);
        return;
      }

      const token = localStorage.getItem('yegoNewsToken');
      const articleId = article.id || article._id;
      if (token && articleId) {
        axios.get(`${BACKEND_URL}/api/auth/saved-articles`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(res => {
          const savedIds = (res.data?.articles || res.data || []).map(a => String(a.id || a._id));
          setIsBookmarked(savedIds.includes(String(articleId)));
        })
        .catch(() => setIsBookmarked(false));
      } else {
        setIsBookmarked(false);
      }
    };

    checkBookmarkStatus();

    const handleSavedEvent = (e) => {
      const targetId = String(article.id || article._id || article.link || article.url || article.title || '');
      if (e.detail?.articleId && String(e.detail.articleId) === targetId) {
        setIsBookmarked(Boolean(e.detail.saved));
      } else {
        checkBookmarkStatus();
      }
    };

    window.addEventListener('articleSaved', handleSavedEvent);
    window.addEventListener('yego_saved_articles_changed', handleSavedEvent);
    return () => {
      window.removeEventListener('articleSaved', handleSavedEvent);
      window.removeEventListener('yego_saved_articles_changed', handleSavedEvent);
    };
  }, [article]);

  // Fetch author details
  useEffect(() => {
    const authorKey = article?.author_id || article?.author || article?.source;
    if (!authorKey) {
      setAuthorData(null);
      return;
    }
    axios.get(`${BACKEND_URL}/api/authors/${encodeURIComponent(authorKey)}`)
      .then(res => {
        if (res.data) setAuthorData(res.data);
      })
      .catch(() => setAuthorData(null));
  }, [article?.author_id, article?.author, article?.source]);

  // Handle Like
  const handleLike = async () => {
    if (!isAuthenticated) {
      showToast('Please sign in to like articles', 'warning');
      return;
    }
    setLikeLoading(true);
    try {
      const token = localStorage.getItem('yegoNewsToken');
      const response = await axios.post(
        `${BACKEND_URL}/api/articles/${article.id}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setLikes(response.data.like_count);
      setUserLiked(response.data.liked);
      showToast(response.data.liked ? 'Liked!' : 'Like removed', 'like');
    } catch (error) {
      console.error('Like failed:', error);
      showToast('Failed to like', 'error');
    } finally {
      setLikeLoading(false);
    }
  };

  // Handle Save
  const handleSaveArticle = async () => {
    if (!article) return;
    setBookmarkLoading(true);
    try {
      const savedNow = saveArticleLocally(article);
      setIsBookmarked(savedNow);

      const articleId = article.id || article._id;
      const token = localStorage.getItem('yegoNewsToken');
      if (token && articleId) {
        const endpoint = savedNow ? '/api/auth/save-article' : '/api/auth/unsave-article';
        await axios.post(`${BACKEND_URL}${endpoint}?article_id=${articleId}`, { article_id: articleId }, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      }
      showToast(savedNow ? 'Article saved to library' : 'Article removed from library', 'info');
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      showToast('Failed to update bookmark', 'error');
    } finally {
      setBookmarkLoading(false);
    }
  };

  // Handle Share
  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: article?.title,
          url: url,
        });
      } catch (err) {}
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);
        showToast('Link copied to clipboard', 'info');
      } catch (err) {
        showToast('Failed to copy link', 'error');
      }
    }
  };

  const scrollToComments = () => {
    const el = document.getElementById('comments-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h1>
        <p className="text-gray-600 mb-6">The article you&apos;re looking for doesn&apos;t exist or has been removed.</p>
        <Link 
          to="/" 
          className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl transition-colors"
        >
          Back to Home
        </Link>
      </div>
    );
  }

  const isInternalArticle = Boolean(article.is_user_created || article.content);
  const displayTitle = article.title;
  const displayContent = article.content || article.description || '';
  const authorName = authorData?.name || article.author || article.source || 'YEGO Staff';
  const authorRole = authorData?.role === 'admin' ? 'Editor' : 'Author';
  const authorAvatar = authorData?.profile_picture_url || authorData?.avatar || article.author_image || article.author_photo || (
    (user && (user.id === article.author_id || user.name === article.author || user.name === article.source))
      ? (user.profile_picture_url || user.avatar)
      : null
  );
  const articleUrl = `https://yegonews.com/news/${slug}`;
  const articleImage = article.image_url || 'https://yegonews.com/og-image.jpg';
  const publishedDate = article.published_at || article.created_at || new Date().toISOString();

  const getPanelStyle = () => {
    if (!isMobile) return {};

    if (isSwiping) {
      return {
        transform: `translate3d(${Math.max(0, swipeOffset)}px, 0, 0)`,
        transition: 'none',
        willChange: 'transform',
        contain: 'paint',
      };
    }

    if (isSnappingBack) {
      return {
        transform: 'translate3d(0, 0, 0)',
        transition: 'transform 200ms cubic-bezier(0.25, 1, 0.5, 1)',
        willChange: 'transform',
        contain: 'paint',
      };
    }

    if (isExiting) {
      const exitDuration = exitConfig?.duration || 240;
      return {
        transform: 'translate3d(100%, 0, 0)',
        transition: `transform ${exitDuration}ms cubic-bezier(0.25, 1, 0.5, 1)`,
        willChange: 'transform',
        contain: 'paint',
      };
    }

    return {
      transform: 'translate3d(0, 0, 0)',
      contain: 'paint',
      willChange: 'transform',
    };
  };

  const getPanelAnimationClass = () => {
    if (!isMobile || isSwiping || isSnappingBack || isExiting) return '';
    return 'animate-mobile-panel-in';
  };

  return (
    <>
      <Helmet>
        <title>{`${displayTitle} | Yegonews`}</title>
        <meta name="description" content={(article.description || article.quick_read || displayTitle).substring(0, 160)} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content={authorName} />
        <meta name="theme-color" content="#ffffff" />
        <link rel="canonical" href={articleUrl} />
        <meta property="og:title" content={displayTitle} />
        <meta property="og:description" content={(article.description || article.quick_read || displayTitle).substring(0, 160)} />
        <meta property="og:image" content={articleImage} />
        <meta property="og:url" content={articleUrl} />
        <meta property="og:type" content="article" />
      </Helmet>

      <div 
        className={`min-h-screen bg-white md:bg-[#F8FAFC] dark:bg-gray-950 dark:md:bg-gray-950 text-gray-900 dark:text-gray-100 flex flex-col ${getPanelAnimationClass()}`}
        style={getPanelStyle()}
        {...(isMobile ? swipeHandlers : {})}
      >
        <div className="w-full md:py-6 md:px-4 lg:px-6 flex-1 flex flex-col">
          {/* Main Feed-Style Container (max-w-7xl like Homefeed) */}
          <div className="w-full max-w-7xl mx-auto bg-white dark:bg-gray-900 md:border md:border-gray-200/90 dark:md:border-gray-800 md:rounded-2xl md:shadow-sm flex flex-col flex-1 overflow-hidden">
          {/* Header Bar matching ReaderPanel */}
          <header className="sticky top-0 z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-100 dark:border-gray-800 flex items-center justify-between px-4 sm:px-6 lg:px-8 py-3 shadow-sm select-none">
            {/* Left side - Back Arrow + Avatar + Source Name */}
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <button
                onClick={handleDismiss}
                className="p-1.5 -ml-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full flex-shrink-0 text-gray-700 dark:text-gray-200 transition-colors"
                aria-label="Go back"
              >
                <ChevronLeft className="h-6 w-6 text-gray-800 dark:text-gray-200" />
              </button>
              <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm overflow-hidden">
                {authorAvatar ? (
                  <img src={authorAvatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  article.source?.charAt(0) || 'Y'
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-900 dark:text-white truncate leading-tight">{article.source || 'YEGO'}</p>
              </div>
            </div>

            {/* Right side - Sign in/User, External Link & Close */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {!isAuthenticated ? (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="px-3.5 py-1.5 text-xs font-semibold rounded-full bg-emerald-500 hover:bg-emerald-600 text-white transition-colors shadow-sm"
                >
                  Sign In
                </button>
              ) : (
                <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-bold overflow-hidden border border-white dark:border-gray-800 shadow-sm">
                  {user?.avatar ? (
                    <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    user?.name?.charAt(0).toUpperCase() || 'U'
                  )}
                </div>
              )}
              {!isInternalArticle && article.link && (
                <a
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-700 dark:text-gray-200"
                  title="Open original article"
                >
                  <ExternalLink className="h-5 w-5" />
                </a>
              )}
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-700 dark:text-gray-200"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </header>

          {/* Content Container - 2-Column YouTube Layout on Desktop */}
          <main className="flex-1 w-full px-4 sm:px-6 lg:px-8 py-5 sm:py-7 bg-white dark:bg-gray-900">
            <div className="lg:flex lg:gap-8 xl:gap-10 items-start">
              {/* Left Column: Main Article */}
              <div className="flex-1 min-w-0">
                {/* Category Tag Pill at Top of Article */}
            {article.category && (
              <div className="mb-2.5">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 text-[11px] font-semibold rounded-full border border-emerald-100 dark:border-emerald-800/60">
                  <Tag className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                  <span>{article.category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                </span>
              </div>
            )}

            {/* Title - Modern, high-contrast, fully theme-adaptive */}
            <h1 className="text-xl sm:text-2xl md:text-[26px] font-bold text-gray-900 dark:text-white tracking-tight leading-snug mb-3 text-left font-sans">
              {displayTitle}
            </h1>

            {/* Byline with Icons */}
            <div className="flex flex-wrap items-center gap-3 text-[11.5px] text-gray-500 dark:text-gray-400 mb-3.5">
              <div className="flex items-center gap-1">
                <User className="h-3.5 w-3.5 text-gray-400 dark:text-gray-400" />
                <span className="font-medium text-gray-700 dark:text-gray-200">{authorName}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5 text-gray-400 dark:text-gray-400" />
                <span>{(() => {
                  const d = parseDate(publishedDate) || new Date();
                  return d.toLocaleDateString('en-US', {
                    month: 'short', 
                    day: 'numeric',
                    year: 'numeric'
                  });
                })()}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-400" />
                <span>{calculateReadTime(displayContent)}</span>
              </div>
            </div>

            {/* Action Pills Bar */}
            <div className="flex items-center gap-2 mb-5 pb-1 overflow-x-auto no-scrollbar">
              <button
                onClick={handleSaveArticle}
                disabled={bookmarkLoading}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold transition-colors shadow-sm flex-shrink-0 ${
                  isBookmarked
                    ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                    : 'bg-white dark:bg-gray-800/90 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100'
                }`}
              >
                <Bookmark className={`h-3.5 w-3.5 ${isBookmarked ? 'fill-current text-emerald-600 dark:text-emerald-400' : 'text-gray-600 dark:text-gray-400'}`} />
                <span>{isBookmarked ? 'Saved' : 'Save'}</span>
              </button>

              <button
                onClick={handleShare}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/90 hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 transition-colors shadow-sm flex-shrink-0"
              >
                <Share2 className="h-3.5 w-3.5 text-gray-600 dark:text-gray-400" />
                <span>Share</span>
              </button>

              <button
                onClick={scrollToComments}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/90 hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 transition-colors shadow-sm flex-shrink-0"
              >
                <MessageCircle className="h-3.5 w-3.5 text-gray-600 dark:text-gray-400" />
                <span>{commentCount}</span>
              </button>

              <button
                onClick={handleLike}
                disabled={likeLoading}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold transition-colors shadow-sm flex-shrink-0 ${
                  userLiked
                    ? 'bg-red-50 dark:bg-red-950/60 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800'
                    : 'bg-white dark:bg-gray-800/90 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100'
                }`}
              >
                <Heart className={`h-3.5 w-3.5 ${userLiked ? 'fill-current text-red-500' : 'text-gray-600 dark:text-gray-400'}`} />
                <span>{likes}</span>
              </button>
            </div>

          {/* Hero Image - 9to5Google Floating Green Card Style */}
          {article.image_url && (
            <div className="mb-6">
              <div className="relative my-4 mx-2.5 sm:mx-3 group/img">
                {/* Top-Left Green Frame Accent */}
                <div 
                  className="absolute -top-2.5 -left-2.5 w-1/4 h-[calc(100%+10px)] border-t-[2.5px] border-l-[2.5px] border-emerald-600 dark:border-emerald-500 pointer-events-none z-0" 
                  aria-hidden="true" 
                />
                {/* Bottom-Right Green Frame Accent */}
                <div 
                  className="absolute -bottom-2.5 -right-2.5 w-[calc(100%+10px)] h-1/4 border-b-[2.5px] border-r-[2.5px] border-emerald-600 dark:border-emerald-500 pointer-events-none z-0" 
                  aria-hidden="true" 
                />

                {/* Foreground Image Card */}
                <div className="relative z-10 w-full overflow-hidden bg-white dark:bg-gray-900 shadow-sm border border-gray-200/60 dark:border-gray-800">
                  <img
                    src={article.image_url}
                    alt={displayTitle}
                    className="w-full h-auto max-h-[500px] object-cover group-hover/img:scale-[1.01] transition-transform duration-300"
                    loading="eager"
                  />
                </div>
              </div>

              {(article.photo_credit || article.caption) ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 px-2.5">
                  Photo Credit: {article.photo_credit || article.caption}
                </p>
              ) : article.source ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 px-2.5">
                  Photo: {article.source}
                </p>
              ) : null}
            </div>
          )}

          {/* Article Body Content */}
          <div className="text-[#111827] dark:text-gray-100">
            {displayContent.split(/(\[IMAGE:[^\]]+\]|\[VIDEO:[^\]]+\]|\[H2:[^\]]+\]|\[BOLD:[^\]]+\])/).map((part, idx) => {
              if (!part) return null;

              // Image - Standard clean image
              const imageMatch = part.match(/\[IMAGE:([^\]]+)\]/);
              if (imageMatch) {
                return (
                  <div key={idx} className="my-5">
                    <img 
                      src={imageMatch[1]} 
                      alt="Article content" 
                      className="w-full h-auto max-h-[550px] object-contain bg-gray-50 dark:bg-gray-900 rounded-xl shadow-sm"
                      loading="lazy"
                    />
                  </div>
                );
              }
              
              const videoMatch = part.match(/\[VIDEO:([^\]]+)\]/);
              if (videoMatch) {
                const videoUrl = videoMatch[1];
                const isEmbed = videoUrl.includes('youtube.com/embed') || videoUrl.includes('vimeo.com');
                return (
                  <div key={idx} className="my-5">
                    {isEmbed ? (
                      <iframe
                        src={videoUrl}
                        className="w-full aspect-video rounded-xl shadow-sm"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        src={videoUrl}
                        controls
                        className="w-full max-h-[500px] rounded-xl bg-black"
                      />
                    )}
                  </div>
                );
              }
              
              const headingMatch = part.match(/\[H2:([^\]]+)\]/);
              if (headingMatch) {
                return (
                  <h2 key={idx} className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mt-5 mb-2 tracking-tight">
                    {headingMatch[1]}
                  </h2>
                );
              }
              
              const boldMatch = part.match(/\[BOLD:([^\]]+)\]/);
              if (boldMatch) {
                return <strong key={idx} className="font-bold text-gray-900 dark:text-white">{boldMatch[1]}</strong>;
              }
              
              if (!part.trim()) return null;

              // Split by newlines so consecutive paragraphs are properly separated without excessive whitespace
              const paras = part.split(/\n+/).map(p => p.trim()).filter(Boolean);
              return paras.map((paraText, pIdx) => (
                <p key={`${idx}-${pIdx}`} className="text-gray-800 dark:text-gray-200 text-[14px] sm:text-[15px] leading-[1.65] mb-3 font-normal">
                  {paraText}
                </p>
              ));
            })}
          </div>

          {/* Borderless Editorial Author Signature */}
          <div 
            onClick={() => setAuthorPanelOpen(true)}
            className="pt-8 pb-4 border-t border-gray-150 dark:border-gray-800/80 flex items-start sm:items-center justify-between gap-4 cursor-pointer group select-none"
            title="View all articles by this author"
            role="button"
            tabIndex={0}
          >
            <div className="flex items-center gap-3.5 sm:gap-4 min-w-0">
              {/* Author Avatar with subtle status ring */}
              <div className="relative flex-shrink-0">
                <div className="w-12 h-12 sm:w-13 sm:h-13 rounded-full overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold flex items-center justify-center text-lg ring-2 ring-emerald-500/20 dark:ring-emerald-500/30 group-hover:ring-emerald-500 transition-all">
                  {authorAvatar ? (
                    <img 
                      src={authorAvatar} 
                      alt={authorName} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <span>{authorName.charAt(0).toUpperCase()}</span>
                  )}
                </div>
              </div>

              {/* Author Details */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/70 px-2 py-0.5 rounded-full">
                    {authorRole}
                  </span>
                  {authorData?.article_count ? (
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                      • {authorData.article_count} {authorData.article_count === 1 ? 'story' : 'stories'}
                    </span>
                  ) : null}
                </div>

                <h4 className="text-base font-bold text-gray-900 dark:text-white truncate leading-snug group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                  {authorName}
                </h4>

                {(authorData?.bio || article.author_bio) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 leading-relaxed">
                    {authorData?.bio || article.author_bio}
                  </p>
                )}
              </div>
            </div>

            {/* Right side CTA link */}
            <div className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400 group-hover:underline transition-all">
              <span className="hidden sm:inline">All stories</span>
              <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </div>

          {/* Comments Section */}
          <div id="comments-section" className="mt-8 pt-6 border-t border-gray-150 dark:border-gray-800/80">
            <CommentsSection 
              articleId={article.id || article._id} 
              onOpenAuth={() => setShowAuthModal(true)}
              onCommentCountChange={setCommentCount}
            />
          </div>

        </div>

        {/* Vertical Dividing Line between Main Article and Vertical Feed */}
        <div className="hidden lg:block w-px self-stretch bg-gray-200/90 dark:bg-gray-800 flex-shrink-0" />

        {/* Right Column: Suggested Articles Sidebar (YouTube Style) */}
        <div className="hidden lg:block w-80 xl:w-[380px] 2xl:w-[420px] flex-shrink-0">
          <SuggestedArticlesSidebar
            currentArticleId={article.id}
            relatedArticles={relatedArticles}
            recentArticles={recentArticles}
            trendingArticles={trendingArticles}
            onArticleClick={(selectedArticle) => {
              const newSlug = selectedArticle.slug || generateSlug(selectedArticle.title);
              navigate(`/news/${newSlug}`);
            }}
          />
        </div>
      </div>
    </main>
          </div>
        </div>

        {/* Main App Footer */}
        <Footer />
      </div>

      {/* Author Slide-out Panel */}
      <AuthorPanel
        authorId={article.author_id || authorData?.id || article.author || article.source || 'Admin'}
        authorData={authorData}
        isOpen={authorPanelOpen}
        onClose={() => setAuthorPanelOpen(false)}
      />

      {/* Auth Modal for Sign In / Sign Up */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
};

export default ArticlePage;
